import { motion } from "framer-motion";
import { Droplets, CheckCircle2, AlertTriangle, OctagonAlert } from "lucide-react";

const STATUS = {
  ok: { label: "Oil healthy", icon: CheckCircle2, text: "text-emerald-400", border: "border-emerald-500/40", bar: "bg-emerald-500", bg: "bg-emerald-500/10" },
  due_soon: { label: "Oil change due soon", icon: AlertTriangle, text: "text-amber-500", border: "border-amber-600/50", bar: "bg-amber-500", bg: "bg-amber-600/10" },
  overdue: { label: "Oil change overdue", icon: OctagonAlert, text: "text-red-400", border: "border-red-500/50", bar: "bg-red-500", bg: "bg-red-500/10" },
};

const fmtDate = (d) =>
  d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" }) : "—";

const Stat = ({ label, value, testid }) => (
  <div data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="mt-1 font-mono text-sm text-slate-100">{value}</div>
  </div>
);

export const OilTrac = ({ data }) => {
  if (!data) return null;
  const s = STATUS[data.status] || STATUS.ok;
  const pct = data.percent_used ?? 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`mt-6 border ${s.border} ${s.bg} p-5`}
      data-testid="oil-trac-panel"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <Droplets size={18} className={s.text} />
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-slate-200">Oil Trac</span>
        </div>
        <span className={`inline-flex items-center gap-1.5 border ${s.border} px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.15em] ${s.text}`} data-testid="oil-trac-status">
          <s.icon size={12} /> {s.label}
        </span>
      </div>

      <div className="mt-5 h-1.5 w-full overflow-hidden bg-white/10" data-testid="oil-trac-bar">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className={`h-full ${s.bar}`}
        />
      </div>
      <div className="mt-2 flex justify-between font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
        <span>Changed {fmtDate(data.last_change_date)}{data.last_change_km != null ? ` · ${data.last_change_km.toLocaleString()} km` : ""}</span>
        <span>{data.percent_used != null ? `${pct}% of interval used` : `${data.interval_km.toLocaleString()} km interval`}</span>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="Oil grade" value={data.oil_grade || "—"} testid="oil-trac-grade" />
        <Stat label="Next due km" value={data.next_due_km != null ? `${data.next_due_km.toLocaleString()} km` : "—"} testid="oil-trac-due-km" />
        <Stat label="Next due date" value={fmtDate(data.next_due_date)} testid="oil-trac-due-date" />
        <Stat
          label="Remaining"
          testid="oil-trac-remaining"
          value={
            data.km_remaining != null
              ? `${Math.max(0, data.km_remaining).toLocaleString()} km`
              : data.days_remaining != null
                ? `${Math.max(0, data.days_remaining)} days`
                : "—"
          }
        />
      </div>
    </motion.div>
  );
};
